import React from 'react';
import {
  isNodeEmpty,
  NodeType,
  PresentationNode
} from '../../../../../api/models/Case/PresentationModel';
import { FachdatenV2Field } from './FachdatenV2Field';
import styles from '../Fachdaten/Fachdaten.module.css';
import {
  Section,
  Spacer,
  Stack,
  Text
} from '@efast_public/fjd-component-library';

const NODE_TYPE_LABELS_TO_SUPRESS_IN_ARRAY = new Set<NodeType>([
  NodeType.String,
  NodeType.File
]);

interface FachdatenV2NodeProps {
  readonly node: PresentationNode;
  readonly depthLevel: number;
  readonly showInSection?: boolean;
  readonly hideEmpty?: boolean;
  readonly suppressLabel?: boolean;
  readonly showApplicantLabel: boolean;
}

export function FachdatenV2Node({
  node,
  depthLevel,
  showInSection,
  hideEmpty,
  suppressLabel,
  showApplicantLabel
}: FachdatenV2NodeProps) {
  switch (node.nodeType) {
    case NodeType.String:
    case NodeType.Date:
      return (
        <FachdatenV2Field
          label={showApplicantLabel ? node.applicantLabel : node.label}
          displayValue={node.value}
          showInSection={showInSection}
          suppressLabel={suppressLabel}
          id={`labelValue_antragsdaten_${depthLevel}`}
        />
      );

    case NodeType.Boolean:
      return (
        <FachdatenV2Field
          label={showApplicantLabel ? node.applicantLabel : node.label}
          displayValue={node.value === null ? null : node.value ? 'Ja' : 'Nein'}
          showInSection={showInSection}
          suppressLabel={suppressLabel}
          id={`labelValue_antragsdaten_${depthLevel}`}
        />
      );

    case NodeType.Select:
      return (
        <FachdatenV2Field
          label={showApplicantLabel ? node.applicantLabel : node.label}
          displayValue={node.selectedDisplayLabel}
          showInSection={showInSection}
          suppressLabel={suppressLabel}
          id={`labelValue_antragsdaten_${depthLevel}`}
        />
      );

    case NodeType.File: {
      const displayValue = node.fileName ?? node.fileId;
      const label = showApplicantLabel ? node.applicantLabel : node.label;
      if (displayValue === null) {
        return (
          <FachdatenV2Field
            label={label}
            displayValue={node.fileName}
            showInSection={showInSection}
            suppressLabel={suppressLabel}
            id={`labelValue_antragsdaten_${depthLevel}`}
          />
        );
      }
      const description = node.description && (
        <Text italic> ({`Dokument: ${node.description}`})</Text>
      );
      if (suppressLabel) {
        return (
          <Stack
            id={`labelValue_antragsdaten_${depthLevel}`}
            spacing={'xs'}
            direction="row"
          >
            <Text>{displayValue}</Text>
            {description}
          </Stack>
        );
      }

      return (
        <Stack
          id={`labelValue_antragsdaten_${depthLevel}`}
          spacing={'xs'}
          key={Math.random()}
        >
          <Text strong>{`${label}:`}</Text>
          <Stack
            id={`labelValue_antragsdaten_${depthLevel}`}
            spacing={'xs'}
            key={Math.random()}
            direction="row"
          >
            <Text>{displayValue}</Text>
            {description}
          </Stack>
        </Stack>
      );
    }

    case NodeType.Group: {
      let entries = Object.entries(node.children);
      if (hideEmpty) {
        entries = entries.filter(([, child]) => !isNodeEmpty(child));
      }
      if (hideEmpty && entries.length === 0) return null;
      const label = showApplicantLabel ? node.applicantLabel : node.label;
      return (
        <Section key={Math.random()} type="section">
          <Text strong>{`${label}:`}</Text>
          <Spacer size="l" />
          <div className={styles.nestedGroup}>
            <Section.Content>
              <Stack orientation="column" spacing="l">
                {entries.map(([key, child]) => (
                  <FachdatenV2Node
                    key={key}
                    node={child}
                    depthLevel={depthLevel + 1}
                    showInSection={depthLevel === 1}
                    hideEmpty={hideEmpty}
                    showApplicantLabel={showApplicantLabel}
                  />
                ))}
              </Stack>
            </Section.Content>
          </div>
        </Section>
      );
    }

    case NodeType.Array: {
      let items = node.items;
      if (items.length === 0) {
        if (hideEmpty) return null;
        const label = showApplicantLabel ? node.applicantLabel : node.label;
        if (label) {
          return (
            <FachdatenV2Field
              label={label}
              displayValue={null}
              showInSection={showInSection}
              suppressLabel={suppressLabel}
              id={`labelValue_antragsdaten_${depthLevel}`}
            />
          );
        }
      }

      if (hideEmpty) {
        items = items.filter((item) => !isNodeEmpty(item));
      }

      const allSameLabel = showApplicantLabel
        ? items.length > 0 &&
          items.every((item) => item.applicantLabel === items[0].applicantLabel)
        : items.length > 0 &&
          items.every((item) => item.label === items[0].label);

      const allSuppressableTypes =
        items.length > 0 &&
        items.every((item) =>
          NODE_TYPE_LABELS_TO_SUPRESS_IN_ARRAY.has(item.nodeType)
        );
      const suppressLabels = allSuppressableTypes && allSameLabel;

      return (
        <Section key={Math.random()}>
          {suppressLabels && <Text strong>{`${items[0].label}:`}</Text>}
          <Stack
            spacing={'s'}
            key={Math.random()}
            className={suppressLabels ? styles.nestedGroup : undefined}
          >
            {items.map((item, idx) => (
              <FachdatenV2Node
                key={idx}
                node={item}
                depthLevel={depthLevel + 1}
                showInSection={false}
                hideEmpty={hideEmpty}
                suppressLabel={suppressLabels}
                showApplicantLabel={showApplicantLabel}
              />
            ))}
          </Stack>
        </Section>
      );
    }
  }
}

export default FachdatenV2Node;
